import { zodResolver } from '@hookform/resolvers/zod';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import is from '@sindresorhus/is';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { FilterButton } from './FilterButton';
import { FilterInput } from './FilterInput';
import { filterStateSchema } from '../../../../../shared/source/data-contract/filter-state';
import { SideDrawerContent } from '../../../design/components/drawer/SideDrawer';
import { onEnterKeyDownPreventDefault } from '../form';

import type { FilterWithFilterStates } from '../../../../../shared/source/data-contract/filter-state';
import type { FunctionComponent } from 'react';

const filterSchema = z.object({
    id: z.string(),
    filterStates: filterStateSchema.array(),
});

type FilterSidebarProps = {
    readonly filters: readonly FilterWithFilterStates[];
    readonly onResetFilters?: () => void;
    readonly onSubmitFilters?: (filters: readonly FilterWithFilterStates[]) => void;
};

export const FilterSidebar: FunctionComponent<FilterSidebarProps> = ({ filters, onResetFilters, onSubmitFilters }) => {
    const hasActiveFilters = filters.some(({ filterStates }) =>
        filterStates.some((filter) => !(is.nullOrUndefined(filter.value) || is.emptyArray(filter.value))),
    );

    const {
        control,
        formState: { isDirty },
        handleSubmit,
    } = useForm({
        resolver: zodResolver(filterSchema.array()),
        values: filters,
    });

    return (
        <form
            style={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                justifyContent: 'space-between',
            }}
            onKeyDown={onEnterKeyDownPreventDefault}
            onSubmit={handleSubmit((filters) => {
                onSubmitFilters?.(filters);
            })}
        >
            <Grid>
                {filters.map((filter, filterIndex) => {
                    const { id, filterStates, subTitle, title } = filter;

                    return (
                        <SideDrawerContent key={id} subTitle={subTitle} title={title}>
                            <Grid
                                container={true}
                                flex={1}
                                flexDirection="column"
                                gap={3}
                                sx={{
                                    isolation: 'isolate',
                                    p: 2,
                                }}
                            >
                                {filterStates.map((filterState, filterStateIndex) => (
                                    <FilterInput
                                        key={`${id}/${filterIndex}/${filterStateIndex}`}
                                        control={control}
                                        fieldName={`${filterIndex}.filterStates.${filterStateIndex}.value`}
                                        filterState={filterState}
                                    />
                                ))}
                            </Grid>
                        </SideDrawerContent>
                    );
                })}
            </Grid>
            <Grid
                container={true}
                sx={{
                    backgroundColor: 'background.paper',
                    bottom: 0,
                    flexDirection: 'column',
                    position: 'sticky',
                }}
            >
                <Divider />
                <Box
                    sx={{
                        px: 2,
                        py: 1,
                    }}
                >
                    <FilterButton hasFilters={hasActiveFilters} isDirty={isDirty} onReset={onResetFilters} />
                </Box>
            </Grid>
        </form>
    );
};
